import { api, type ReviewContext } from '../api';
import { callout, field, input, opt, openDialog, select, todayISO, val, numVal } from '../dialog';
import { esc, html, toast } from '../ui';

/**
 * The airworthiness review, as a sequence of acts rather than a form.
 *
 * Each step is recorded when it happens, because that is how a review is
 * actually carried out — records one day, the aircraft another. The certificate
 * is a separate act at the end, and the dialog states which form is coming and
 * under which article before anyone signs, because on a Part-M aircraft
 * without the privilege the answer is "none, the authority issues it".
 */

/**
 * The certificate the aircraft arrived with.
 *
 * Not a review — a transcription. An aircraft taken on mid-cycle has a valid
 * ARC issued by whoever managed it before, and until this existed the record
 * said "no ARC on file" while a certificate sat in the aircraft's file with an
 * expiry date everybody was planning around.
 */
export function recordExistingArc(ctx: ReviewContext, reload: () => void): void {
  const form = ctx.planned.form ?? 'FORM_15C';
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Record a certificate issued elsewhere',
    sub: `${ctx.registration} — transcribed from the aircraft's file, not issued here`,
    primary: 'Record it',
    body: () => html`
      <div class="frow">
        ${field('Form', select('formKind', [
          opt('FORM_15C', 'EASA Form 15c — issued by a CAMO or CAO', form === 'FORM_15C'),
          opt('FORM_15B', 'EASA Form 15b — issued by the competent authority', form === 'FORM_15B'),
          opt('FORM_15A', 'EASA Form 15a — recommendation', form === 'FORM_15A'),
        ].join('')))}
        ${field('Reference', input('reference', '', 'placeholder="ARC-2026-014" style="font-family:var(--mono)" maxlength="40"'))}
      </div>
      <div class="frow3">
        ${field('Issued on', input('issuedAt', '', 'type="date"'))}
        ${field('Expires on', input('expiresAt', '', 'type="date"'))}
        ${field('Extensions already used', select('extensionNumber', [
          opt('0', 'None — the original certificate'),
          opt('1', 'One'),
          opt('2', 'Two — no more are available'),
        ].join('')))}
      </div>
      ${field('Issued by', input('issuedByAuthority', '', 'placeholder="Romanian CAA, or the CAMO that held the aircraft" maxlength="160"'))}
      ${callout('caution', '!', html`
        Nobody signs this. It is a copy of a certificate somebody else issued, so no authorisation is
        checked and none is claimed — what it does is put the expiry date where the fleet list, the
        review screen and the planning window can all see it.
        <br><br>
        Extending it from here is not available: an extension is a privilege exercised over a review this
        organisation carried out. Carry out the review when it falls due.
      `)}
    `,
    collect: (root) => {
      const reference = val(root, 'reference').trim();
      if (!reference) return { error: 'The certificate has a reference — record it as written.' };
      const issuedAt = val(root, 'issuedAt');
      const expiresAt = val(root, 'expiresAt');
      if (!issuedAt || !expiresAt) return { error: 'Both dates are on the certificate.' };
      if (expiresAt <= issuedAt) return { error: 'It expires before it was issued — check the dates.' };
      const issuedByAuthority = val(root, 'issuedByAuthority').trim();
      if (!issuedByAuthority) return { error: 'Who issued it? A certificate with no issuer is not one.' };
      return {
        formKind: val(root, 'formKind'),
        reference,
        issuedAt,
        expiresAt,
        issuedByAuthority,
        extensionNumber: Number(val(root, 'extensionNumber') || 0),
      };
    },
    submit: (payload) => api.reviews.recordArc(ctx.aircraftId, payload),
    done: (r) => `${r.reference} on file for ${r.registration} — expires ${String(r.expiresAt).slice(0, 10)}`,
    onDone: reload,
  });
}

export function startReview(ctx: ReviewContext, reload: () => void): void {
  openDialog<{ reviewerPersonId: string; startedAt?: string }>({
    title: 'Start airworthiness review',
    sub: `${ctx.registration} — ${ctx.typeModel}`,
    primary: 'Start review',
    body: () => html`
      ${field('Review staff', select('reviewerPersonId',
        ctx.reviewers.map((r) => opt(r.id, `${r.displayName}${r.licence ? ` — ${r.licence}` : ''}`)).join('')),
        `Must hold a current ${esc(ctx.planned.needs)} authorisation`)}
      ${field('Started', input('startedAt', todayISO(), 'type="date"'))}
      ${callout(ctx.planned.recommends ? 'caution' : 'info', ctx.planned.recommends ? '!' : 'i', html`
        <b>${esc(ctx.planned.formLabel)}</b> — ${esc(ctx.planned.basis)}. ${esc(ctx.planned.note)}
      `)}
      ${ctx.blockers.length
        ? callout('exceed', '✕', html`
            This aircraft cannot have a certificate issued today: ${esc(ctx.blockers.join('; '))}. The review
            can still be carried out, but it cannot be completed until these are cleared.
          `)
        : ''}
    `,
    collect: (root) => {
      const reviewerPersonId = val(root, 'reviewerPersonId');
      if (!reviewerPersonId) return { error: 'Nobody available holds the authorisation this review needs.' };
      return { reviewerPersonId, startedAt: val(root, 'startedAt') || undefined };
    },
    submit: (payload) => api.aircraft.startReview(ctx.aircraftId, payload),
    done: (r) => `${r.reference} opened`,
    onDone: reload,
  });
}

/** Records the review's own steps — what was checked, and when. */
export function recordSteps(ctx: ReviewContext, reload: () => void): void {
  const r = ctx.openReview!;
  openDialog<Record<string, unknown>>({
    wide: true,
    title: `Review ${r.reference}`,
    sub: 'Record each step as it is carried out',
    primary: 'Save progress',
    body: () => html`
      <div class="fset">
        <div class="lg">Records</div>
        <div class="frow">
          ${field('Records review complete', input('recordsReviewedAt', r.recordsReviewedAt?.slice(0, 10) ?? '', 'type="date"'),
            'The continuing airworthiness record set')}
          ${field('Directives verified', input('directivesVerifiedAt', r.directivesVerifiedAt?.slice(0, 10) ?? '', 'type="date"'),
            'AD and SB status checked against the published state')}
        </div>
      </div>

      <div class="fset">
        <div class="lg">Sample check of release documents</div>
        <div class="frow3">
          ${field('Sampled', input('sampleSize', r.sampleSize ?? '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
          ${field('Population', input('samplePopulation', r.samplePopulation ?? '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
          ${field('Checked', input('sampleCheckedAt', r.sampleCheckedAt?.slice(0, 10) ?? '', 'type="date"'))}
        </div>
      </div>

      <div class="fset">
        <div class="lg">Physical survey</div>
        <div class="frow">
          ${field('Surveyed', input('physicalSurveyAt', r.physicalSurveyAt?.slice(0, 10) ?? '', 'type="date"'))}
          ${field('Location', input('physicalSurveyLocation', r.physicalSurveyLocation ?? '', 'placeholder="LRBS hangar 3" maxlength="160"'))}
        </div>
      </div>

      <div class="fset">
        <div class="lg">Findings</div>
        <div class="frow">
          ${field('Raised', input('findingsRaised', r.findingsRaised, 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
          ${field('Cleared', input('findingsCleared', r.findingsCleared, 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
        </div>
        ${callout('info', 'i', html`
          The certificate cannot be issued while a finding from this review is still open.
        `)}
      </div>

      ${field('Notes', input('notes', '', 'maxlength="4000"'))}
    `,
    collect: (root) => ({
      recordsReviewedAt: val(root, 'recordsReviewedAt') || undefined,
      directivesVerifiedAt: val(root, 'directivesVerifiedAt') || undefined,
      sampleSize: numVal(root, 'sampleSize'),
      samplePopulation: numVal(root, 'samplePopulation'),
      sampleCheckedAt: val(root, 'sampleCheckedAt') || undefined,
      physicalSurveyAt: val(root, 'physicalSurveyAt') || undefined,
      physicalSurveyLocation: val(root, 'physicalSurveyLocation') || undefined,
      findingsRaised: numVal(root, 'findingsRaised'),
      findingsCleared: numVal(root, 'findingsCleared'),
      notes: val(root, 'notes') || undefined,
    }),
    submit: (payload) => api.reviews.update(r.id, payload),
    done: () => `${r.reference} updated`,
    onDone: reload,
  });
}

/** Completes the review: issue, recommend, or refuse. */
export function completeReview(ctx: ReviewContext, reload: () => void): void {
  const r = ctx.openReview!;
  let outcome = ctx.planned.outcome === 'REFUSED' ? 'REFUSED' : ctx.planned.outcome;

  openDialog<{ outcome: string; reference?: string; notes?: string }>({
    wide: true,
    title: `Complete review ${r.reference}`,
    sub: `${ctx.registration} — ${ctx.planned.formLabel}`,
    primary: ctx.planned.recommends ? 'Record recommendation' : 'Issue certificate',
    body: () => html`
      ${field('Outcome', select('outcome', [
        ...(ctx.planned.outcome === 'ISSUE' ? [opt('ISSUE', `Issue ${ctx.planned.formLabel}`, outcome === 'ISSUE')] : []),
        ...(ctx.planned.outcome === 'RECOMMEND' ? [opt('RECOMMEND', 'Recommend to the authority', outcome === 'RECOMMEND')] : []),
        opt('REFUSED', 'Refuse — the aircraft does not meet the requirements', outcome === 'REFUSED'),
      ].join(''), 'data-outcome'))}

      ${outcome === 'ISSUE'
        ? html`
            ${field('Certificate reference', input('reference', '', 'placeholder="Leave blank to allocate automatically" style="font-family:var(--mono)" maxlength="40"'))}
            ${callout('ok', '✓', html`
              <b>${esc(ctx.planned.formLabel)}</b> under ${esc(ctx.planned.basis)}, valid one year from today and
              extendable twice by a year each. Signed by
              ${esc(ctx.reviewers.find((x) => x.id === r.id)?.displayName ?? 'the review staff on this review')}.
            `)}
          `
        : outcome === 'RECOMMEND'
          ? callout('caution', '!', html`
              No certificate is created here. The recommendation goes to the authority, which issues
              <b>Form 15a</b>. Record the certificate once the authority has issued it.
            `)
          : callout('exceed', '✕', html`
              The review closes without a certificate. This is a real outcome and is recorded as such —
              say why in the notes.
            `)}

      ${field('Notes', input('notes', '', 'maxlength="4000"'))}
    `,
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-outcome]')?.addEventListener('change', (e) => {
        outcome = (e.target as HTMLSelectElement).value;
        refresh();
      });
    },
    collect: (root) => ({
      outcome: val(root, 'outcome'),
      reference: val(root, 'reference') || undefined,
      notes: val(root, 'notes') || undefined,
    }),
    submit: (payload) => api.reviews.complete(r.id, payload),
    done: (res) => (res.arc ? `${res.arc.reference} issued — expires ${res.arc.expiresAt.slice(0, 10)}` : res.note),
    onDone: (res: { notBilled?: string | null }) => {
      // The certificate is issued either way; the fee going unrecorded is not
      // something the certificate itself would ever show.
      if (res.notBilled) toast(res.notBilled, 'error');
      reload();
    },
  });
}

/** ML.A.901(c) — one more year, twice at most. */
export function extendArc(ctx: ReviewContext, reload: () => void): void {
  const arc = ctx.currentArc!;
  openDialog<{ extendedByPersonId: string; controlledEnvironmentConfirmed: boolean }>({
    title: 'Extend the airworthiness review certificate',
    sub: `${arc.reference} — expires ${arc.expiresAt.slice(0, 10)}`,
    primary: 'Extend by one year',
    body: () => html`
      ${field('Extended by', select('extendedByPersonId',
        ctx.reviewers.map((p) => opt(p.id, `${p.displayName}${p.licence ? ` — ${p.licence}` : ''}`)).join('')))}
      <label style="display:flex;gap:9px;align-items:flex-start;font-size:13.5px">
        <input type="checkbox" name="confirm" style="margin-top:3px">
        <span>
          I confirm the aircraft has remained in a controlled environment — continuously managed, with its
          maintenance programme kept up — for the whole of the preceding period.
        </span>
      </label>
      ${callout('info', 'i', html`
        ${esc(ctx.extension.basis)} permits two extensions of one year each. This is
        <b>extension ${ctx.extension.used + 1} of 2</b>; the new expiry runs from the current one, not from
        today. After the second, a full review is required.
      `)}
    `,
    collect: (root) => {
      const person = val(root, 'extendedByPersonId');
      if (!person) return { error: 'Nobody available holds the authorisation to extend this certificate.' };
      const confirmed = root.querySelector<HTMLInputElement>('[name="confirm"]')?.checked ?? false;
      if (!confirmed) {
        return { error: 'An extension is only available while the aircraft has remained in a controlled environment.' };
      }
      return { extendedByPersonId: person, controlledEnvironmentConfirmed: true };
    },
    submit: (payload) => api.reviews.extend(arc.id, payload),
    done: (r) =>
      `${r.arc.reference} — extended to ${r.arc.expiresAt.slice(0, 10)}, ${r.extensionsRemaining} extension(s) left`,
    onDone: reload,
  });
}
